// Picks which version of a report to show (design §2.1, §7).

import { useId } from 'react'
import { formatDateTime } from '../../lib/format'
import type { ReportVersionSummary } from './report-types'

export interface ReportVersionPickerProps {
  versions: ReportVersionSummary[]
  /** Version being shown. */
  value: number
  currentVersion: number
  onChange: (version: number) => void
  disabled?: boolean
}

export function ReportVersionPicker({
  versions,
  value,
  currentVersion,
  onChange,
  disabled,
}: ReportVersionPickerProps) {
  const id = useId()
  const sorted: VersionOption[] = [...versions].sort(
    (a, b) => b.version - a.version
  )
  // Always offer the shown version, even if the list hasn't loaded it.
  if (!sorted.some((entry) => entry.version === value)) {
    sorted.unshift({ version: value, message: null, createdAt: '' })
  }

  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-sm text-ink-3">
        Version
      </label>
      <select
        id={id}
        className="field-input w-auto max-w-[22rem] py-1 text-sm"
        // Not the field a drawer focuses on open (it would steal the arrow keys).
        data-no-autofocus
        value={value}
        disabled={disabled || sorted.length < 2}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {sorted.map((entry) => (
          <option key={entry.version} value={entry.version}>
            {versionLabel(entry, currentVersion)}
          </option>
        ))}
      </select>
    </div>
  )
}

type VersionOption = Pick<
  ReportVersionSummary,
  'version' | 'message' | 'createdAt'
>

function versionLabel(
  entry: VersionOption,
  currentVersion: number
): string {
  const parts = [`v${entry.version}`]
  if (entry.version === currentVersion) parts[0] += ' (latest)'
  if (entry.createdAt) parts.push(formatDateTime(entry.createdAt))
  if (entry.message) {
    parts.push(
      entry.message.length > 60
        ? `${entry.message.slice(0, 60)}…`
        : entry.message
    )
  }
  return parts.join(' · ')
}
